import { TFunction } from 'i18next';
import { DialogViewModel } from '../../../../hooks/useDrawer';
import { ReceiptsViewModel } from '../../../../viewModels/receipts';
import { computed, makeObservable, observable } from 'mobx';
import { TextField } from '../../../../lib/components';

export type ReceiptAction = 'delete' | 'approve' | 'reject' | 'settle';

export type ReceiptActionDialogConfirmDetails = { action?: ReceiptAction; note?: string };

export class ReceiptActionDialogViewModel extends DialogViewModel<
  ReceiptsViewModel[],
  ReceiptAction,
  ReceiptActionDialogConfirmDetails
> {
  note?: string;

  constructor(protected translate: TFunction) {
    super();
    makeObservable(this, {
      applicable: computed,
      selectedCount: computed,
      applicableCount: computed,
      type: computed,
      text: computed,
      title: computed,
      confirmButtonText: computed,
      note: observable
    });
  }

  protected get action() {
    return this.args;
  }

  get applicable() {
    if (!this.item || !this.action) return [];
    return this.item.filter((r) => r.isApplicable(this.args as ReceiptAction));
  }

  get selectedCount() {
    return this.item?.length ?? 0;
  }

  get applicableCount() {
    return this.applicable.length;
  }

  get type() {
    return (this.selectedCount ?? this.applicableCount) <= this.applicableCount
      ? 'TOTAL'
      : 'PARTIAL';
  }

  get title() {
    return this.translate(`receipts:ACTION_DIALOG_TITLE_${this.type}`, {
      context: this.action,
      count: this.applicableCount,
      totalCount: this.selectedCount
    });
  }

  get text() {
    return this.action === 'delete' || this.action === 'settle'
      ? this.type === 'TOTAL'
        ? this.translate('receipts:DELETE_DIALOG_TEXT')
        : this.translate('receipts:DELETE_DIALOG_INFO')
      : '';
  }

  get confirmButtonText() {
    return this.applicableCount > 0
      ? this.translate('receipts:CONFIRM_ACTION', {
          context: this.action,
          count: this.applicableCount
        })
      : this.translate('common:BACK');
  }

  get cancelButtonText() {
    return this.translate('common:CANCEL');
  }

  get contentComponent() {
    return this.action === 'reject' ? (
      <TextField
        label={this.translate('common:NOTE')}
        onChange={(e) => (this.note = e.target.value)}
      />
    ) : undefined;
  }

  get variant() {
    return this.action === 'delete' ? 'delete' : 'warning';
  }

  protected override getConfirmDetails() {
    return { action: this.action, note: this.note };
  }

  protected override getConfirmedItems() {
    return this.applicable;
  }
}
